// src/hooks/useProjectMembers.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useStableAuth } from '@/hooks/useStableAuth';
import { toast } from 'sonner';
import type { ProjectMember, ProjectMemberRole } from '@/types/project';
import { projectKeys } from './useProjects';

export const projectMemberKeys = {
  all: ['project-members'] as const,
  byProject: (projectId: string) =>
    [...projectMemberKeys.all, projectId] as const,
};

export function useProjectMembers(projectId: string | undefined) {
  const { userId, companyId } = useStableAuth();
  const queryClient = useQueryClient();

  const invalidate = () => {
    queryClient.invalidateQueries({
      queryKey: projectMemberKeys.byProject(projectId ?? ''),
    });
    // Também invalida o detalhe do projeto (member_count)
    if (projectId) {
      queryClient.invalidateQueries({
        queryKey: projectKeys.detail(projectId),
      });
      queryClient.invalidateQueries({ queryKey: projectKeys.all });
    }
  };

  // ── Listagem de membros com dados de perfil ────────────────────────────────
  const membersQuery = useQuery({
    queryKey: projectMemberKeys.byProject(projectId ?? ''),
    queryFn: async (): Promise<ProjectMember[]> => {
      const { data, error } = await supabase
        .from('project_members')
        .select(`
          id,
          project_id,
          user_id,
          role,
          added_by,
          added_at,
          profiles!project_members_user_id_fkey(
            full_name,
            avatar_url,
            user_position
          )
        `)
        .eq('project_id', projectId!);

      if (error) throw error;

      return (data ?? []).map((row: any) => ({
        id: row.id,
        project_id: row.project_id,
        user_id: row.user_id,
        role: row.role as ProjectMemberRole,
        added_by: row.added_by,
        added_at: row.added_at,
        full_name: row.profiles?.full_name ?? null,
        avatar_url: row.profiles?.avatar_url ?? null,
        user_position: row.profiles?.user_position ?? null,
      }));
    },
    enabled: !!projectId && !!userId && !!companyId,
  });

  // ── Adicionar membro ───────────────────────────────────────────────────────
  const addMemberMutation = useMutation({
    mutationFn: async ({
      memberUserId,
      role = 'editor',
    }: {
      memberUserId: string;
      role?: ProjectMemberRole;
    }): Promise<string> => {
      const { data, error } = await supabase.rpc('add_project_member', {
        p_project_id: projectId!,
        p_user_id: memberUserId,
        p_role: role,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: () => {
      invalidate();
      toast.success('Membro adicionado ao projeto.');
    },
    onError: (error: Error) => {
      console.error('Erro ao adicionar membro:', error);
      toast.error('Erro ao adicionar membro. Tente novamente.');
    },
  });

  // ── Alterar papel ──────────────────────────────────────────────────────────
  const updateRoleMutation = useMutation({
    mutationFn: async ({
      memberId,
      role,
    }: {
      memberId: string;
      role: ProjectMemberRole;
    }): Promise<void> => {
      const { error } = await supabase
        .from('project_members')
        .update({ role })
        .eq('id', memberId);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast.success('Papel atualizado.');
    },
    onError: (error: Error) => {
      console.error('Erro ao atualizar papel:', error);
      toast.error('Erro ao atualizar papel.');
    },
  });

  // ── Remover membro ─────────────────────────────────────────────────────────
  const removeMemberMutation = useMutation({
    mutationFn: async (memberId: string): Promise<void> => {
      const { error } = await supabase
        .from('project_members')
        .delete()
        .eq('id', memberId);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast.success('Membro removido do projeto.');
    },
    onError: (error: Error) => {
      console.error('Erro ao remover membro:', error);
      toast.error('Erro ao remover membro.');
    },
  });

  // Papel do usuário atual no projeto
  const myRole = membersQuery.data?.find((m) => m.user_id === userId)?.role ?? null;
  const isOwner = myRole === 'owner';
  const canEdit = myRole === 'owner' || myRole === 'editor';

  return {
    members: membersQuery.data ?? [],
    isLoading: membersQuery.isLoading,
    myRole,
    isOwner,
    canEdit,
    addMember: addMemberMutation.mutateAsync,
    isAddingMember: addMemberMutation.isPending,
    updateRole: updateRoleMutation.mutateAsync,
    isUpdatingRole: updateRoleMutation.isPending,
    removeMember: removeMemberMutation.mutateAsync,
    isRemovingMember: removeMemberMutation.isPending,
  };
}
